import {CommonModule} from '@angular/common';
import {Component, EventEmitter, forwardRef, Input, OnChanges, OnInit, Output, ViewChild,} from '@angular/core';
import {ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR,} from '@angular/forms';
import {MatSelect, MatSelectModule} from '@angular/material/select';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatChipsModule} from '@angular/material/chips';
import {MatIconModule} from '@angular/material/icon';

@Component({
  selector: 'app-dropdown-card-field',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatChipsModule,
    MatIconModule,
  ],
  templateUrl: './dropdown-card-field.component.html',
  styleUrls: ['./dropdown-card-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DropdownCardFieldComponent),
      multi: true,
    },
  ],
})
export class DropdownCardFieldComponent<T>
  implements ControlValueAccessor, OnInit, OnChanges {
  @ViewChild('select', {static: true}) select!: MatSelect;

  @Input() label = '';
  @Input() placeholder = '';
  @Input() disabled = false;
  @Input() items: readonly T[] = [];
  @Input() multiple = true;
  @Input() displayWith: (item: T) => string = (i: any) => `${i ?? ''}`;
  @Input() compareWith?: (a: T, b: T) => boolean = (a, b) => a === b;
  @Input() searchable = true;
  @Input() searchFn: (item: T, term: string) => boolean = (item, term) =>
    this.displayWith(item)
      .toLowerCase()
      .includes((term ?? '').toLowerCase().trim());
  @Input() panelClass: string | string[] = 'dropdown-card-field__panel';
  @Input() fieldAriaLabel?: string;

  @Output() selectionChange = new EventEmitter<T[]>();

  searchTerm = '';
  filteredItems: T[] = [];
  value: T[] = [];

  private onChange: (value: T[]) => void = () => {
  };
  private onTouched: () => void = () => {
  };

  registerOnChange(fn: (value: T[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  ngOnInit() {
    this.filteredItems = [...this.items];
  }

  ngOnChanges() {
    this.applySearch();
  }

  onSearch(term: string) {
    this.searchTerm = (term ?? '').toLowerCase().trim();
    this.applySearch();
  }

  private applySearch(): void {
    const term = this.searchTerm?.trim().toLowerCase();
    const result = !term
      ? [...this.items]
      : this.items.filter(item => this.searchFn(item, term));
    
    for (const selected of this.value ?? []) {
      const alreadyInList = result.some(item =>
        this.compareWith?.(item, selected)
      );

      if (!alreadyInList) {
        result.push(selected);
      }
    }

    this.filteredItems = result;
  }

  writeValue(value: T[] | null): void {
    this.value = Array.isArray(value) ? [...value] : [];
  }

  openSelect(): void {
    if (!this.disabled) this.select.open();
  }

  onSelection(values: T[]): void {
    this.value = values ?? [];
    this.onChange(this.value);
    this.selectionChange.emit(this.value);
  }

  removeAt(index: number): void {
    if (this.disabled) return;
    this.value = this.value.filter((_, i) => i !== index);
    this.onChange(this.value);
    this.selectionChange.emit(this.value);
  }

  matches(item: T): boolean {
    return !this.searchable || this.searchFn(item, this.searchTerm);
  }

  stop(e: Event): void {
    e.stopPropagation();
  }

  touched(): void {
    this.onTouched();
  }

  trackByIndex = (i: number) => i;
}
